/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { matchPath, useHistory, useLocation, useRouteMatch } from 'react-router';
import { LuisSidebar, LuisSidebarProps } from 'src/core/components/LuisSidebar';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { LuisSidebarNormalItemProps } from 'src/core/types/luisSidebar';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { sidePaneMessages } from 'src/luis/appShell/appPage/components/sidebarMessages';
import { useReviewEndpointUtterancesCoachmark } from 'src/luis/appShell/appPage/components/useReviewEndpointUtterancesCoachmark';

type UrlMatchParams = {
    /**
     * The selected page in case of build or manage pages.
     */
    selectedPage?: string;

    /**
     * The currently selected pane.
     */
    pane: 'dashboard' | 'build' | 'manage';
};

/**
 * A presentational side panel component used
 * in the AppShell component.
 */
export const LuisAppSidebar = observer(() => {
    const history = useHistory();
    const match = useRouteMatch();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { coachmark, dismiss: dismissCoachmark, ref: reviewEndpointUtterancesRef } = useReviewEndpointUtterancesCoachmark();

    const isMobileView = !isXLargeOrGreater;
    const currentPane = matchPath<UrlMatchParams>(pathname, `${match.url}/:pane/:selectedPage?`)?.params.pane;
    /**
     * @todo Removes this when back-end supports this page for E0 users
     */
    const isReviewLinksHidden = rootStore.azureStore.session.csAccountStore?.sku.code === 'E0';

    const appId = rootStore.currentLuisAppStore.id;
    const versionId = rootStore.currentLuisAppStore.activeVersion;

    const improveAppPerformanceItems: LuisSidebarNormalItemProps[] = [
        {
            text: localize(sidePaneMessages.reviewEndpointUtterances),
            onClick: () => {
                history.push(routeTable.luis.reviewUtterancesPage(appId, versionId));
                dismissCoachmark();
            },
            isSelected: doesRouteMatchLocation(routeTable.luis.reviewUtterancesPage(appId, versionId), {
                locationToMatch: pathname
            }),
            ref: reviewEndpointUtterancesRef
        },
        {
            text: localize(sidePaneMessages.phraseListFeature),
            onClick: () => history.push(routeTable.luis.phraseListsPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.phraseListsPage(appId, versionId), { locationToMatch: pathname })
        },
        {
            text: localize(sidePaneMessages.patterns),
            onClick: () => history.push(routeTable.luis.patternsPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.patternsPage(appId, versionId), { locationToMatch: pathname })
        }
    ];

    const buildItems: LuisSidebarNormalItemProps[] = [
        {
            text: localize(sidePaneMessages.appAssets),
            iconName: 'BuildQueue',
            items: [
                {
                    text: localize(sidePaneMessages.intents),
                    dataAutomationId: 'AppAssetsIntentsButton',
                    onClick: () => history.push(routeTable.luis.intentsPage(appId, versionId)),
                    isSelected: doesRouteMatchLocation(routeTable.luis.intentsPage(appId, versionId), {
                        locationToMatch: pathname
                    })
                },
                {
                    text: localize(sidePaneMessages.entities),
                    dataAutomationId: 'AppAssetsEntitiesButton',
                    onClick: () => history.push(routeTable.luis.entitiesPage(appId, versionId)),
                    isSelected: doesRouteMatchLocation(routeTable.luis.entitiesPage(appId, versionId), {
                        locationToMatch: pathname
                    })
                },
                {
                    text: localize(sidePaneMessages.prebuiltDomains),
                    onClick: () => history.push(routeTable.luis.domainsPage(appId, versionId)),
                    isSelected: doesRouteMatchLocation(routeTable.luis.domainsPage(appId, versionId), {
                        locationToMatch: pathname
                    })
                }
            ],
            onClick: () => history.push(routeTable.luis.intentsPage(appId, versionId))
        },
        {
            text: localize(sidePaneMessages.improveAppPerformance),
            iconName: 'Equalizer',
            items: isReviewLinksHidden
                ? improveAppPerformanceItems.filter(item => item.text !== 'Review endpoint utterances')
                : improveAppPerformanceItems,
            onClick: () => history.push(routeTable.luis.reviewUtterancesPage(appId, versionId))
        }
    ];

    const manageItems: LuisSidebarNormalItemProps[] = [
        {
            text: localize(sidePaneMessages.settings),
            onClick: () => history.push(routeTable.luis.applicationInfoPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.applicationInfoPage(appId, versionId), {
                locationToMatch: pathname
            }),
            iconName: 'Settings'
        },
        {
            text: localize(sidePaneMessages.publishSettings),
            onClick: () => history.push(routeTable.luis.publishSettingsPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.publishSettingsPage(appId, versionId), {
                locationToMatch: pathname
            }),
            iconName: 'TriangleRight12'
        },
        {
            text: localize(sidePaneMessages.azureResources),
            onClick: () => history.push(routeTable.luis.azureResourcesPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.azureResourcesPage(appId, versionId), {
                locationToMatch: pathname
            }),
            iconName: 'AzureLogo'
        },
        {
            text: localize(sidePaneMessages.versions),
            onClick: () => history.push(routeTable.luis.versionsPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.versionsPage(appId, versionId), { locationToMatch: pathname }),
            iconName: 'AppIconDefault'
        }
    ];

    const mobileViewItems: LuisSidebarNormalItemProps[] = [
        {
            text: localize(sidePaneMessages.dashboard),
            onClick: () => history.push(routeTable.luis.dashboardPage(appId, versionId)),
            isSelected: doesRouteMatchLocation(routeTable.luis.dashboardPage(appId, versionId), { locationToMatch: pathname }),
            iconName: 'StackedColumnChart2'
        },
        {
            text: localize(sidePaneMessages.build),
            onClick: () => history.push(routeTable.luis.buildPage(appId, versionId)),
            items: buildItems,
            isSelected: doesRouteMatchLocation(routeTable.luis.buildPage(appId, versionId), { locationToMatch: pathname }),
            iconName: 'WebAppBuilderFragment'
        },
        {
            text: localize(sidePaneMessages.manage),
            onClick: () => history.push(routeTable.luis.managePage(appId, versionId), { locationToMatch: pathname }),
            items: manageItems.map(item => ({
                text: item.text,
                onClick: item.onClick,
                isSelected: item.isSelected
            })),
            isSelected: doesRouteMatchLocation(routeTable.luis.managePage(appId, versionId), { locationToMatch: pathname }),
            iconName: 'DataManagementSettings'
        }
    ];

    let sideBarItemLists: LuisSidebarProps['itemLists'] = [];
    if (isMobileView) {
        sideBarItemLists = [mobileViewItems];
    } else {
        switch (currentPane) {
            case 'build':
                sideBarItemLists = [buildItems];
                break;
            case 'manage':
                sideBarItemLists = [manageItems];
                break;
            default:
        }
    }

    return (
        (currentPane !== 'dashboard' || isMobileView) && (
            <>
                <LuisSidebar itemLists={sideBarItemLists} />
                {coachmark}
            </>
        )
    );
});
